import { CanCreate } from 'labstep-web/components/Entity/Can';
import type {
  ResourceItemActionBulkCreateModalContainerProps,
  ResourceItemActionBulkCreateModalProps,
} from 'labstep-web/components/ResourceItem/Action/BulkCreate/Modal/type';
import { FieldType } from 'labstep-web/constants/form';
import { ICONS } from 'labstep-web/constants/icons';
import { EntityBatchProcessCreateContainer } from 'labstep-web/containers/Entity/BatchProcessCreate';
import { DataGridContextProvider } from 'labstep-web/contexts/grid';
import { ActionComponent } from 'labstep-web/core/Action/Component';
import { Button } from 'labstep-web/core/Button';
import type { FieldWithoutActionProps } from 'labstep-web/core/Form/Reusable/types';
import { ModalDefaultContainer as Modal } from 'labstep-web/core/Modal/Default';
import { GridBatchCreateResourceItems } from 'labstep-web/grid/BatchCreateResourceItems';
import {
  BatchCreateItemProvider,
  useBatchCreateItemContext,
} from 'labstep-web/grid/BatchCreateResourceItems/context';
import { GridBatchCreateResourceItemsService } from 'labstep-web/grid/BatchCreateResourceItems/services/grid-batch-create-resource-items-service';
import { Resource } from 'labstep-web/models/resource.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import { RESOURCE_ITEM_IMPORT_ROWS_LIMIT } from 'labstep-web/services/resource-item-import.service';
import type React from 'react';
import { useEffect } from 'react';
import { number } from 'yup';

export const fieldQuantity: FieldWithoutActionProps = {
  fieldType: FieldType.Text,
  name: 'quantity',
  fieldLabel: 'Number of Items',
  validation: number()
    .required()
    .integer()
    .max(RESOURCE_ITEM_IMPORT_ROWS_LIMIT)
    .min(1),
};

export const ResourceItemActionBulkCreateModal: React.FC<
  ResourceItemActionBulkCreateModalProps
> = ({
  resource,
  orderRequest,
  open = false,
  onClose,
  create,
  actionComponentProps,
  onImport,
  status,
  showViewComponent = true,
  ...rest
}) => {
  const context = useBatchCreateItemContext();

  if (!context) {
    return null;
  }

  const { data, setData, dateFormats } = context;

  useEffect(() => {
    // Initialize data when it's empty
    if (data.length === 0) {
      const initialData = orderRequest
        ? GridBatchCreateResourceItemsService.getInitialData(
            resource,
            orderRequest.remainingItemsCount,
            orderRequest,
          )
        : GridBatchCreateResourceItemsService.getInitialData(
            resource,
            1,
          );
      setData(initialData);
    }
  }, [data.length, orderRequest, resource, setData]);

  return (
    <Modal
      dataCy="ResourceItem/Action/BulkCreate/Modal"
      header={() => <span>Add Items</span>}
      footer={({ toggleModal }) => {
        return (
          <Button
            primary
            onClick={() => {
              if (
                GridBatchCreateResourceItemsService.checkValidData(
                  data,
                )
              ) {
                const postData =
                  GridBatchCreateResourceItemsService.convertToPostData(
                    data,
                    dateFormats,
                  );
                if (postData.length === 0) {
                  GridBatchCreateResourceItemsService.triggerValidationError(
                    'Please add at least one item',
                  );
                  return;
                }
                create(postData as any, {
                  toast: true,
                });
                onImport?.();
                setData([]);
                toggleModal();
              } else {
                GridBatchCreateResourceItemsService.triggerValidationError(
                  'Missing required fields',
                );
              }
            }}
            status={status}
          >
            Add Items
          </Button>
        );
      }}
      closeIcon={true}
      initialModalState={open}
      onClose={({ toggleModal }) => {
        setData([]);
        toggleModal();
      }}
      size="fullscreen"
      content={() => {
        return (
          <DataGridContextProvider>
            <GridBatchCreateResourceItems resource={resource} />
          </DataGridContextProvider>
        );
      }}
      viewComponent={
        showViewComponent &&
        (({ toggleModal }) => (
          <ActionComponent
            type="text"
            icon={ICONS.resource_item.primary}
            text={`Add ${ResourceItem.getHumanReadableEntityName(
              true,
              true,
              resource.is_template,
            )}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleModal();
            }}
            {...actionComponentProps}
          />
        ))
      }
      {...rest}
    />
  );
};

export const ResourceItemActionBulkCreateModalContainer: React.FC<
  ResourceItemActionBulkCreateModalContainerProps
> = (props) => {
  return (
    <CanCreate
      entityName={ResourceItem.entityName}
      parentName={Resource.entityName}
      parentId={props.resource.id}
    >
      <EntityBatchProcessCreateContainer
        entityName={ResourceItem.entityName}
        title="Creating items..."
      >
        {({ create }) => (
          <BatchCreateItemProvider>
            <ResourceItemActionBulkCreateModal
              create={create}
              {...props}
            />
          </BatchCreateItemProvider>
        )}
      </EntityBatchProcessCreateContainer>
    </CanCreate>
  );
};
